import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Panel } from '@promanage/ui';
import { LoadFailed } from '../components/QueryState';
import { TopBar } from '../components/TopBar';
import { ListPage, blankDraft, type ListDraft, type SavedList } from '../features/lists/ListPage';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/communications/lists/$id')({
  component: SavedListPage,
});

/** A stored list as the form's starting point. One that no longer parses opens with its columns reset. */
function draftOf(list: SavedList): ListDraft {
  return {
    name: list.name,
    base: list.base,
    shared: list.shared,
    definition: list.definition ?? blankDraft(list.base).definition,
  };
}

function SavedListPage() {
  const { id } = Route.useParams();
  const list = useQuery(
    trpc.lists.get.queryOptions(
      { id },
      { meta: { errorTitle: 'Could not load that list' }, staleTime: 10_000 },
    ),
  );

  if (list.error) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="List" subtitle="Not found, or not shared with you" />
        <LoadFailed retry={() => void list.refetch()} />
      </div>
    );
  }
  if (!list.data) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="List" subtitle="Loading…" />
        <Panel className="h-40" />
      </div>
    );
  }

  // Keyed by when it was last saved, so a save resets the page's idea of "unchanged" to what is stored.
  return <ListPage key={list.data.updatedAt} saved={list.data} initial={draftOf(list.data)} />;
}
